/**
 * Ana render geçişi (render faz).
 *
 *   - render override varsa yalnızca onu çağırır.
 *   - Aktif TSL effect varsa PostProcessingController.renderTsl().
 *   - Effect yoksa veya TSL build/render başarısızsa düz beauty:
 *     classic EffectComposer veya renderer.render(scene, camera).
 */
import { WebGLRenderer } from "three";
import { noteWebglFrame } from "../debug/RenderTrace";
import { engineProfiling } from "../EngineProfiling";
import type { Engine } from "../Engine";
import type { MaterialGeometryCache } from "../MaterialGeometryCache";
import type { PostProcessingController } from "../postprocessing/PostProcessingController";
import type { RenderSceneSwapCoordinator } from "../RenderSceneSwapCoordinator";
import type { EngineSystem, FrameContext, FramePhase } from "../runtime/EngineSystem";

export class MainRenderSystem implements EngineSystem {
  readonly name = "mainRender";
  readonly labels: Partial<Record<FramePhase, string>> = {
    render: "renderScene",
  };

  private _renderOverride?: (engine: Engine) => void;
  private _lastWebGLMainFrameStats: { calls: number; triangles: number } | null =
    null;
  private _savedAutoReset: boolean | undefined;

  constructor(
    private readonly _swap: RenderSceneSwapCoordinator,
    private readonly _cache: MaterialGeometryCache,
    private readonly _post: PostProcessingController,
  ) {}

  setRenderOverride(fn: ((engine: Engine) => void) | undefined): void {
    this._renderOverride = fn;
  }

  get hasRenderOverride(): boolean {
    return !!this._renderOverride;
  }

  getLastWebGLMainFrameStats(): { calls: number; triangles: number } | null {
    return this._lastWebGLMainFrameStats;
  }

  render(ctx: FrameContext): void {
    noteWebglFrame();
    const engine = ctx.engine;

    if (this._renderOverride) {
      this._renderOverride(engine);
      return;
    }

    engineProfiling.instrumentComposer(this._post.composer);
    const logical = engine.activeScene!;
    const useSwap = this._swap.enabled && !!logical;
    const sig = this._cache.getLastStructureSignature();
    const buckets = this._cache.getBuckets();

    const renderStructureRebuilt = useSwap
      ? this._swap.prepareFrame(logical, sig, buckets)
      : false;

    const glInst = engine.renderer.instance;
    let webglMainFrameMeter = false;
    if (glInst instanceof WebGLRenderer) {
      this._savedAutoReset = glInst.info.autoReset;
      glInst.info.autoReset = false;
      glInst.info.reset();
      webglMainFrameMeter = true;
    } else {
      this._lastWebGLMainFrameStats = null;
    }

    try {
      if (useSwap) {
        this._swap.pushAndLog(renderStructureRebuilt, sig, buckets, (s) =>
          this._post.setRenderPassScene(s),
        );
      }

      // No active posts / outline → plain beauty. TSL fail → same fallback
      // (never leave a blank frame when effects are selected but graph breaks).
      const drewTsl =
        this._post.usesTslPipeline && this._post.renderTsl();
      if (!drewTsl) {
        const composer = this._post.composer;
        if (composer) {
          composer.render();
        } else {
          engine.renderer.render(
            this._swap.getWebGLScene(logical),
            engine.activeCamera!.instance,
          );
        }
      }

      if (webglMainFrameMeter && glInst instanceof WebGLRenderer) {
        this._lastWebGLMainFrameStats = {
          calls: glInst.info.render.calls,
          triangles: glInst.info.render.triangles,
        };
      }
    } finally {
      if (useSwap) {
        this._swap.popAndRestoreComposer(logical, (s) =>
          this._post.setRenderPassScene(s),
        );
      }
      if (webglMainFrameMeter && glInst instanceof WebGLRenderer) {
        glInst.info.autoReset = this._savedAutoReset ?? true;
      }
    }
  }
}
